import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LookerEmbedSDK } from "@looker/embed-sdk";
import { Grid, Loader, Tabs } from "@pushpress/shared-components";
import { IReports } from "../../../apis/reportsApi";
import useQuery from "../../../utils/query";
import { useGetReportConfigurationQuery } from "./Looker-generated.hooks";
import styles from "./Looker.module.scss";

export const LOOKER_DASHBOARD_ID = "looker-dashboard";

interface LookerContentProps {
  reportUrl: string;
  dashboardId: string;
}

function LookerContent({ reportUrl, dashboardId }: LookerContentProps) {
  useEffect(() => {
    LookerEmbedSDK.createDashboardWithUrl(reportUrl)
      .appendTo(`#${LOOKER_DASHBOARD_ID}`)
      .withClassName(styles.looker)
      .build()
      .connect();
  }, [reportUrl]);

  return (
    <div
      className={styles.iframeContainer}
      id={LOOKER_DASHBOARD_ID}
      data-cy={dashboardId}
    />
  );
}

interface LookerProps {
  report: IReports;
}

function Looker({ report }: LookerProps) {
  const { queryParams, setQueryParam } = useQuery();
  const isFirstLoad = useRef(true);
  const [dashboardId, setDashboardId] = useState<string | undefined>(
    report.fields.dashboardId,
  );
  const { data, loading } = useGetReportConfigurationQuery({
    variables: {
      getReportConfigurationInput: {
        embedDomain: window.location.origin.includes("localhost")
          ? "localhost"
          : window.location.origin,
        dashboardId: dashboardId!,
      },
    },
    skip: !dashboardId,
    fetchPolicy: "no-cache",
  });

  const handleChangeTab = useCallback(
    (tabIndex: number) => {
      const tab = report.fields?.tabs?.[tabIndex];

      if (tab) {
        setQueryParam("tab", tab.key);
      }
    },
    [setQueryParam, report.fields],
  );

  const selectedTab = useMemo(() => {
    const tabParam = queryParams.get("tab");

    if (!tabParam) return 0;

    return report.fields.tabs?.findIndex((tab) => tab.key === tabParam);
  }, [queryParams, report.fields]);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (!report.fields.dashboardId && report.fields.tabs?.length > 0) {
        const tabParam = queryParams.get("tab");
        if (!tabParam) handleChangeTab(0);
        return;
      }

      setDashboardId(report.fields.dashboardId);
    }
  }, [report.fields, queryParams, handleChangeTab]);

  useEffect(() => {
    if (report.fields.tabs?.length > 0) {
      setDashboardId(report.fields.tabs[selectedTab]?.value);
    }
  }, [selectedTab, report.fields]);

  return (
    <>
      {report?.fields?.tabs && report?.fields?.tabs?.length > 0 && (
        <div className={styles.tabsContainer}>
          <Tabs
            tabHeaders={report.fields.tabs.map((tab) => tab.key)}
            selectedIndex={selectedTab}
            changeTab={handleChangeTab}
            onTabClick={handleChangeTab}
          />
        </div>
      )}
      {loading || !data?.getReportConfiguration?.reportUrl ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          paddingY={3}
        >
          <Grid item>
            <Loader />
          </Grid>
        </Grid>
      ) : (
        <LookerContent
          key={dashboardId}
          reportUrl={data?.getReportConfiguration?.reportUrl}
          dashboardId={dashboardId}
        />
      )}
    </>
  );
}

export default Looker;
